<template>
  <div class="flex flex-col gap-6 p-4 mt-6">
    <!-- Filters -->
    <section
      class="rounded-lg border border-slate-100 bg-white p-4 shadow-sm dark:border-slate-700 dark:bg-slate-800"
    >
      <div class="grid grid-cols-1 gap-3 md:grid-cols-2 xl:grid-cols-5">
        <input
          :value="searchTerm"
          type="text"
          :placeholder="$t('CAMPAIGN_REPORTS.FILTERS.SEARCH_PLACEHOLDER')"
          class="w-full rounded-md border border-slate-200 bg-white px-3 py-2 text-sm text-slate-800 focus:border-omfy-500 focus:outline-none dark:border-slate-600 dark:bg-slate-900 dark:text-slate-100"
          @input="onSearchInput"
        />
        <select
          :value="filters.campaignType"
          class="w-full rounded-md border border-slate-200 bg-white px-3 py-2 text-sm text-slate-800 focus:border-omfy-500 focus:outline-none dark:border-slate-600 dark:bg-slate-900 dark:text-slate-100"
          @change="updateFilter('campaignType', $event.target.value)"
        >
          <option value="">
            {{ $t('CAMPAIGN_REPORTS.FILTERS.ALL_TYPES') }}
          </option>
          <option v-for="type in campaignTypes" :key="type" :value="type">
            {{ typeLabel(type) }}
          </option>
        </select>
        <select
          :value="filters.campaignStatus"
          class="w-full rounded-md border border-slate-200 bg-white px-3 py-2 text-sm text-slate-800 focus:border-omfy-500 focus:outline-none dark:border-slate-600 dark:bg-slate-900 dark:text-slate-100"
          @change="updateFilter('campaignStatus', $event.target.value)"
        >
          <option value="">
            {{ $t('CAMPAIGN_REPORTS.FILTERS.ALL_STATUSES') }}
          </option>
          <option
            v-for="status in campaignStatuses"
            :key="status"
            :value="status"
          >
            {{ statusLabel(status) }}
          </option>
        </select>
        <input
          :value="filters.since"
          type="date"
          :title="$t('CAMPAIGN_REPORTS.FILTERS.DATE_FROM')"
          class="w-full rounded-md border border-slate-200 bg-white px-3 py-2 text-sm text-slate-800 focus:border-omfy-500 focus:outline-none dark:border-slate-600 dark:bg-slate-900 dark:text-slate-100"
          @change="updateFilter('since', $event.target.value)"
        />
        <input
          :value="filters.until"
          type="date"
          :title="$t('CAMPAIGN_REPORTS.FILTERS.DATE_TO')"
          class="w-full rounded-md border border-slate-200 bg-white px-3 py-2 text-sm text-slate-800 focus:border-omfy-500 focus:outline-none dark:border-slate-600 dark:bg-slate-900 dark:text-slate-100"
          @change="updateFilter('until', $event.target.value)"
        />
      </div>
      <div class="mt-3 flex items-center justify-end gap-2">
        <omfy-button
          size="small"
          variant="hollow"
          icon="arrow-clockwise"
          :is-disabled="isLoadingMetrics"
          @click="refresh"
        >
          {{ $t('CAMPAIGN_REPORTS.REFRESH') }}
        </omfy-button>
        <omfy-button
          size="small"
          variant="link"
          icon="dismiss"
          @click="clearFilters"
        >
          {{ $t('CAMPAIGN_REPORTS.FILTERS.CLEAR') }}
        </omfy-button>
      </div>
    </section>

    <!-- Summary -->
    <section class="grid grid-cols-2 gap-4 md:grid-cols-3 xl:grid-cols-6">
      <div
        v-for="card in summaryCards"
        :key="card.key"
        class="rounded-lg border border-slate-100 bg-white p-4 shadow-sm dark:border-slate-700 dark:bg-slate-800"
      >
        <p
          class="text-xs font-medium uppercase tracking-wide text-slate-500 dark:text-slate-400"
        >
          {{ card.label }}
        </p>
        <p class="mt-2 text-2xl font-semibold" :class="card.accent">
          {{ card.value }}
        </p>
      </div>
    </section>

    <!-- Live performance -->
    <section
      class="overflow-hidden rounded-lg border border-slate-100 bg-white shadow-sm dark:border-slate-700 dark:bg-slate-800"
    >
      <header
        class="flex items-center justify-between border-b border-slate-100 px-4 py-3 dark:border-slate-700"
      >
        <h3 class="text-base font-medium text-slate-800 dark:text-slate-100">
          {{ $t('CAMPAIGN_REPORTS.PERFORMANCE.HEADER') }}
        </h3>
        <span v-if="generatedAt" class="text-xs text-slate-400">
          {{ $t('CAMPAIGN_REPORTS.LAST_UPDATED') }}:
          {{ formatDate(generatedAt) }}
        </span>
      </header>

      <div v-if="isLoadingMetrics" class="flex justify-center py-10">
        <omfy-spinner />
      </div>

      <div
        v-else-if="!metrics.length"
        class="px-4 py-10 text-center text-sm text-slate-500 dark:text-slate-400"
      >
        {{ $t('CAMPAIGN_REPORTS.PERFORMANCE.EMPTY') }}
      </div>

      <div v-else class="overflow-x-auto">
        <table
          class="w-full text-left text-sm text-slate-500 dark:text-slate-400"
        >
          <thead
            class="bg-slate-50 text-xs uppercase text-slate-700 dark:bg-slate-700 dark:text-slate-300"
          >
            <tr>
              <th class="px-4 py-3">{{ $t('CAMPAIGN_REPORTS.CAMPAIGN') }}</th>
              <th class="px-4 py-3">
                {{ $t('CAMPAIGN_REPORTS.PERFORMANCE.TYPE') }}
              </th>
              <th class="px-4 py-3">
                {{ $t('CAMPAIGN_REPORTS.PERFORMANCE.STATUS') }}
              </th>
              <th class="px-4 py-3 text-right">
                {{ $t('CAMPAIGN_REPORTS.PERFORMANCE.RECIPIENTS') }}
              </th>
              <th class="px-4 py-3 text-right">
                {{ $t('CAMPAIGN_REPORTS.PERFORMANCE.SENT') }}
              </th>
              <th class="px-4 py-3 text-right">
                {{ $t('CAMPAIGN_REPORTS.PERFORMANCE.DELIVERED') }}
              </th>
              <th class="px-4 py-3 text-right">
                {{ $t('CAMPAIGN_REPORTS.PERFORMANCE.READ') }}
              </th>
              <th class="px-4 py-3 text-right">
                {{ $t('CAMPAIGN_REPORTS.PERFORMANCE.FAILED') }}
              </th>
              <th class="px-4 py-3 text-right">
                {{ $t('CAMPAIGN_REPORTS.PERFORMANCE.REPLIES') }}
              </th>
              <th class="px-4 py-3">
                {{ $t('CAMPAIGN_REPORTS.PERFORMANCE.DELIVERY_RATE') }}
              </th>
              <th class="px-4 py-3">
                {{ $t('CAMPAIGN_REPORTS.PERFORMANCE.READ_RATE') }}
              </th>
              <th class="px-4 py-3 text-right">
                {{ $t('CAMPAIGN_REPORTS.PERFORMANCE.ACTIONS') }}
              </th>
            </tr>
          </thead>
          <tbody>
            <tr
              v-for="metric in metrics"
              :key="metric.id"
              class="border-b bg-white last:border-b-0 dark:border-slate-700 dark:bg-slate-800"
            >
              <td
                class="whitespace-nowrap px-4 py-3 font-medium text-slate-900 dark:text-white"
                :title="`#${metric.display_id}`"
              >
                {{ metric.title }}
              </td>
              <td class="px-4 py-3">{{ typeLabel(metric.campaign_type) }}</td>
              <td class="px-4 py-3">
                <span
                  class="rounded-full px-2 py-0.5 text-xs font-medium"
                  :class="statusClass(metric.campaign_status)"
                >
                  {{ statusLabel(metric.campaign_status) }}
                </span>
              </td>
              <td class="px-4 py-3 text-right">{{ metric.recipients }}</td>
              <td class="px-4 py-3 text-right">{{ metric.sent }}</td>
              <td class="px-4 py-3 text-right">{{ metric.delivered }}</td>
              <td class="px-4 py-3 text-right">{{ metric.read }}</td>
              <td class="px-4 py-3 text-right">{{ metric.failed }}</td>
              <td class="px-4 py-3 text-right">{{ metric.replies }}</td>
              <td class="px-4 py-3">
                <rate-bar :value="metric.delivery_rate" color="bg-green-500" />
              </td>
              <td class="px-4 py-3">
                <rate-bar :value="metric.read_rate" color="bg-blue-500" />
              </td>
              <td class="px-4 py-3 text-right">
                <omfy-button
                  size="tiny"
                  variant="bg-blue-700 hover:bg-blue-800"
                  icon="document"
                  :is-disabled="isGenerating"
                  @click="generateReport(metric.id)"
                >
                  {{ $t('CAMPAIGN_REPORTS.GENERATE') }}
                </omfy-button>
              </td>
            </tr>
          </tbody>
        </table>
      </div>
    </section>

    <!-- Generated reports -->
    <section
      class="overflow-hidden rounded-lg border border-slate-100 bg-white shadow-sm dark:border-slate-700 dark:bg-slate-800"
    >
      <header class="border-b border-slate-100 px-4 py-3 dark:border-slate-700">
        <h3 class="text-base font-medium text-slate-800 dark:text-slate-100">
          {{ $t('CAMPAIGN_REPORTS.GENERATED.HEADER') }}
        </h3>
      </header>

      <div v-if="isLoading" class="flex justify-center py-10">
        <omfy-spinner />
      </div>

      <div
        v-else-if="!campaignsReports.length"
        class="px-4 py-10 text-center text-sm text-slate-500 dark:text-slate-400"
      >
        {{ $t('CAMPAIGN_REPORTS.GENERATED.EMPTY') }}
      </div>

      <div v-else class="overflow-x-auto">
        <table
          class="w-full text-left text-sm text-slate-500 dark:text-slate-400"
        >
          <thead
            class="bg-slate-50 text-xs uppercase text-slate-700 dark:bg-slate-700 dark:text-slate-300"
          >
            <tr>
              <th class="px-4 py-3">{{ $t('CAMPAIGN_REPORTS.CAMPAIGN') }}</th>
              <th class="px-4 py-3">
                {{ $t('CAMPAIGN_REPORTS.CAMPAIGN_CHANNEL') }}
              </th>
              <th class="px-4 py-3">
                {{ $t('CAMPAIGN_REPORTS.CAMPAIGN_SEND_DATE') }}
              </th>
              <th class="px-4 py-3">
                {{ $t('CAMPAIGN_REPORTS.START_REPORT_DATE') }}
              </th>
              <th class="px-4 py-3">
                {{ $t('CAMPAIGN_REPORTS.STATUS.HEADER') }}
              </th>
              <th class="px-4 py-3 text-right" />
            </tr>
          </thead>
          <tbody>
            <tr
              v-for="campaignReport in campaignsReports"
              :key="campaignReport.id"
              class="border-b bg-white last:border-b-0 dark:border-slate-700 dark:bg-slate-800"
            >
              <td
                class="whitespace-nowrap px-4 py-3 font-medium text-slate-900 dark:text-white"
                :title="campaignReport.campaign.display_id"
              >
                {{ campaignReport.campaign.title }}
              </td>
              <td class="px-4 py-3">
                {{ typeLabel(campaignReport.campaign.campaign_type) }}
              </td>
              <td class="px-4 py-3">
                {{ formatDate(campaignReport.campaign.scheduled_at) }}
              </td>
              <td class="px-4 py-3">
                {{ formatDate(campaignReport.created_at) }}
              </td>
              <td class="px-4 py-3">
                <span
                  class="rounded-full px-2 py-0.5 text-xs font-medium"
                  :class="
                    campaignReport.status
                      ? 'bg-green-100 text-green-700 dark:bg-green-500/20 dark:text-green-300'
                      : 'bg-amber-100 text-amber-700 dark:bg-amber-500/20 dark:text-amber-300'
                  "
                >
                  {{
                    campaignReport.status
                      ? $t('CAMPAIGN_REPORTS.STATUS.READY')
                      : $t('CAMPAIGN_REPORTS.STATUS.GENERATING')
                  }}
                </span>
              </td>
              <td class="px-4 py-3 text-right">
                <omfy-button
                  v-if="campaignReport.status"
                  size="small"
                  variant="bg-blue-700 hover:bg-blue-800"
                  icon="download"
                  @click="
                    downloadReport(
                      campaignReport.id,
                      campaignReport.campaign.title
                    )
                  "
                >
                  {{ $t('CAMPAIGN_REPORTS.DOWNLOAD') }}
                </omfy-button>
              </td>
            </tr>
          </tbody>
        </table>
      </div>

      <div
        v-if="!isLoading && campaignsReports.length"
        class="flex items-center justify-between border-t border-slate-100 px-4 py-3 dark:border-slate-700"
      >
        <span class="text-xs text-slate-500 dark:text-slate-400">
          {{
            $t('CAMPAIGN_REPORTS.GENERATED.PAGE_INFO', {
              page: generatedPagination.current_page,
              total: generatedPagination.total_pages,
              count: generatedPagination.total_count,
            })
          }}
        </span>
        <div class="flex items-center gap-2">
          <omfy-button
            size="small"
            variant="hollow"
            :is-disabled="generatedPagination.current_page <= 1"
            @click="previousPage"
          >
            {{ $t('CAMPAIGN_REPORTS.GENERATED.PREVIOUS') }}
          </omfy-button>
          <omfy-button
            size="small"
            variant="hollow"
            :is-disabled="
              generatedPagination.current_page >=
              generatedPagination.total_pages
            "
            @click="nextPage"
          >
            {{ $t('CAMPAIGN_REPORTS.GENERATED.NEXT') }}
          </omfy-button>
        </div>
      </div>
    </section>
  </div>
</template>

<script>
import { mapGetters } from 'vuex';
import RateBar from './components/RateBar.vue';

const CAMPAIGN_TYPES = ['ongoing', 'one_off', 'whatsapp'];
const CAMPAIGN_STATUSES = [
  'active',
  'completed',
  'running',
  'error',
  'paused',
  'cancelled',
];

const STATUS_CLASSES = {
  active:
    'bg-green-100 text-green-700 dark:bg-green-500/20 dark:text-green-300',
  running: 'bg-blue-100 text-blue-700 dark:bg-blue-500/20 dark:text-blue-300',
  completed:
    'bg-slate-100 text-slate-700 dark:bg-slate-500/20 dark:text-slate-300',
  paused:
    'bg-amber-100 text-amber-700 dark:bg-amber-500/20 dark:text-amber-300',
  error: 'bg-red-100 text-red-700 dark:bg-red-500/20 dark:text-red-300',
  cancelled: 'bg-red-100 text-red-700 dark:bg-red-500/20 dark:text-red-300',
};

export default {
  components: { RateBar },
  data() {
    return {
      searchTerm: '',
      searchTimer: null,
    };
  },
  computed: {
    ...mapGetters({
      isLoading: 'campaignsReports/getIsLoading',
      isLoadingMetrics: 'campaignsReports/getIsLoadingMetrics',
      isGenerating: 'campaignsReports/getIsGenerating',
      filters: 'campaignsReports/getFilters',
      campaignsReports: 'campaignsReports/getAllCampaignsReports',
      campaignsReportsMeta: 'campaignsReports/getCampaignsReportsMeta',
      metrics: 'campaignsReports/getMetrics',
      summary: 'campaignsReports/getSummary',
      generatedAt: 'campaignsReports/getGeneratedAt',
    }),
    campaignTypes() {
      return CAMPAIGN_TYPES;
    },
    campaignStatuses() {
      return CAMPAIGN_STATUSES;
    },
    generatedPagination() {
      const meta = this.campaignsReportsMeta || {};
      return {
        current_page: meta.current_page || 1,
        total_pages: meta.total_pages || 1,
        total_count: meta.total_count || 0,
      };
    },
    summaryCards() {
      const { summary } = this;
      return [
        {
          key: 'campaigns',
          label: this.$t('CAMPAIGN_REPORTS.SUMMARY.CAMPAIGNS'),
          value: summary.campaigns ?? 0,
          accent: 'text-slate-800 dark:text-slate-100',
        },
        {
          key: 'recipients',
          label: this.$t('CAMPAIGN_REPORTS.SUMMARY.RECIPIENTS'),
          value: summary.recipients ?? 0,
          accent: 'text-slate-800 dark:text-slate-100',
        },
        {
          key: 'total',
          label: this.$t('CAMPAIGN_REPORTS.SUMMARY.MESSAGES'),
          value: summary.total ?? 0,
          accent: 'text-slate-800 dark:text-slate-100',
        },
        {
          key: 'replies',
          label: this.$t('CAMPAIGN_REPORTS.SUMMARY.REPLIES'),
          value: summary.replies ?? 0,
          accent: 'text-indigo-600 dark:text-indigo-300',
        },
        {
          key: 'delivery_rate',
          label: this.$t('CAMPAIGN_REPORTS.SUMMARY.DELIVERY_RATE'),
          value: `${summary.delivery_rate ?? 0}%`,
          accent: 'text-green-600 dark:text-green-300',
        },
        {
          key: 'failure_rate',
          label: this.$t('CAMPAIGN_REPORTS.SUMMARY.FAILURE_RATE'),
          value: `${summary.failure_rate ?? 0}%`,
          accent: 'text-red-600 dark:text-red-300',
        },
      ];
    },
  },
  created() {
    this.searchTerm = this.filters.search || '';
    this.$store.dispatch('campaignsReports/getCampaignsReportsRequest');
    this.$store.dispatch('campaignsReports/getMetricsRequest');
  },
  beforeDestroy() {
    clearTimeout(this.searchTimer);
  },
  methods: {
    refresh() {
      this.$store.dispatch('campaignsReports/getMetricsRequest');
      this.$store.dispatch('campaignsReports/getCampaignsReportsRequest');
    },
    previousPage() {
      const { current_page: currentPage } = this.generatedPagination;
      if (currentPage > 1) {
        this.$store.dispatch(
          'campaignsReports/getCampaignsReportsRequest',
          currentPage - 1
        );
      }
    },
    nextPage() {
      const { current_page: currentPage, total_pages: totalPages } =
        this.generatedPagination;
      if (currentPage < totalPages) {
        this.$store.dispatch(
          'campaignsReports/getCampaignsReportsRequest',
          currentPage + 1
        );
      }
    },
    updateFilter(key, value) {
      this.$store.dispatch('campaignsReports/setFilters', { [key]: value });
    },
    onSearchInput(event) {
      const value = event.target.value;
      this.searchTerm = value;
      clearTimeout(this.searchTimer);
      this.searchTimer = setTimeout(() => {
        this.updateFilter('search', value);
      }, 400);
    },
    clearFilters() {
      clearTimeout(this.searchTimer);
      this.searchTerm = '';
      this.$store.dispatch('campaignsReports/resetFilters');
    },
    async generateReport(campaignId) {
      try {
        await this.$store.dispatch(
          'campaignsReports/generateReport',
          campaignId
        );
        this.showAlert(this.$t('CAMPAIGN_REPORTS.GENERATE_SUCCESS'));
      } catch (error) {
        this.showAlert(this.$t('CAMPAIGN_REPORTS.GENERATE_FAILED'));
      }
    },
    async downloadReport(id, title) {
      try {
        await this.$store.dispatch('campaignsReports/downloadReport', {
          id,
          title,
        });
      } catch (error) {
        this.showAlert(this.$t('CAMPAIGN_REPORTS.DOWNLOAD_FAILED'));
      }
    },
    typeLabel(type) {
      if (!type) return '—';
      const key = `CAMPAIGN_REPORTS.CAMPAIGN_TYPES.${type}`;
      const translation = this.$t(key);
      return translation === key ? type : translation;
    },
    statusLabel(status) {
      if (!status) return '—';
      const key = `CAMPAIGN_REPORTS.CAMPAIGN_STATUSES.${status}`;
      const translation = this.$t(key);
      return translation === key ? status : translation;
    },
    statusClass(status) {
      return STATUS_CLASSES[status] || STATUS_CLASSES.completed;
    },
    formatDate(rawDate) {
      if (!rawDate) return '—';
      const date = new Date(rawDate);
      if (Number.isNaN(date.getTime())) return '—';
      const pad = value => String(value).padStart(2, '0');
      return `${pad(date.getDate())}/${pad(
        date.getMonth() + 1
      )}/${date.getFullYear()} - ${pad(date.getHours())}:${pad(
        date.getMinutes()
      )}`;
    },
  },
};
</script>
